/* eslint-disable no-restricted-syntax -- Semantic no-media warning + audio thumbnail colours must stay stable across themes */
/**
 * Shared media surface (#2233) — the video/audio playback element
 * that both editors mount inside the floating video overlay or their
 * body panels. Handles:
 *   - ReactPlayer wiring against `useAudio` (play / pause / progress /
 *     playbackRate / mute / setDuration).
 *   - Mobile forceAudio decoding for video sessions (11h <video> on
 *     iOS Safari OOM regression).
 *   - Audio thumbnail on audio-only or forceAudio sessions.
 *   - No-media-available state with an inline `Add media source`
 *     button that opens the shared MediaSourceModal.
 *   - An `overlay` slot rendered on top of the video for
 *     editor-specific chrome (caption text overlay, etc.).
 *
 * Everything else — the transport controls, caption style modal,
 * timing shift — is composition owned by the calling editor.
 */

import ReactPlayer from 'react-player/lazy';
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Link2, Mic } from 'lucide-react';
import audioThumbnail from '@assets/images/audio-thumbnail.png';
import { useAudio } from '@utils/providers/audio-context.tsx';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { useMediaQuery } from '@utils/hooks/use-media-query.tsx';
import MediaSourceModal from '@components/editors/media-source-modal.tsx';
import { Session } from '@utils/types/editors/sessions.ts';

export interface EditorMediaSurfaceProps {
  /** Direct media URL (audio or video). `null`/`undefined` renders the no-media state. */
  mediaUrl: string | null | undefined;
  /** `audio` short-circuits the video element even on desktop. */
  mediaType?: string | null;
  /** Peaks/waveform URL surfaced by session; passed back through `onReady`. */
  waveInfoUrl?: string | null;
  /** Session id used by the inline "Add source" CTA. */
  sessionId?: string | null;
  /** Full session, passed through to MediaSourceModal so it can render the
   *  regenerate-audio-timeline state (stale / pending / failed / mismatch). */
  session?: Session;
  /** Called once ReactPlayer has mounted and the surface is ready.
   *  Also re-fired whenever waveInfoUrl changes (e.g. after
   *  regenerate-audio-timeline swaps it to a new versioned blob) so the
   *  consumer can re-attach peaks against the new URL — ReactPlayer's own
   *  onReady only fires once per media mount. */
  onReady?: (waveInfoUrl?: string | null) => void;
  /**
   * Overlay content painted on top of the video (e.g. caption
   * segments). Rendered inside the aspect-video container so it
   * scales with the surface.
   */
  overlay?: ReactNode;
  /** Optional aspect override — defaults to `aspect-video`. */
  aspectClass?: string;
  /** Extra classes on the outer container. */
  className?: string;
}

export function EditorMediaSurface({
  mediaUrl,
  mediaType,
  waveInfoUrl,
  sessionId,
  session,
  onReady,
  overlay,
  aspectClass = 'aspect-video',
  className
}: EditorMediaSurfaceProps) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const {
    play,
    onMediaPause,
    onProgress,
    setRef,
    isPlaying,
    isMuted,
    playbackRate,
    setDuration
  } = useAudio();

  const ref = useRef<ReactPlayer>(null);
  const [isMediaSourceOpen, setIsMediaSourceOpen] = useState(false);

  useEffect(() => {
    if (ref.current) setRef(ref);
  }, [setRef]);

  const hasMedia = !!mediaUrl;
  const isAudio = !mediaUrl || mediaType === 'audio';
  const [isMobile] = useMediaQuery('(max-width: 768px)');
  const forceAudioPlayback = isMobile && !isAudio;
  const showAudioThumbnail = (isAudio || forceAudioPlayback) && hasMedia;
  const playerConfig = useMemo(
    () => ({ file: { forceAudio: forceAudioPlayback } }),
    [forceAudioPlayback]
  );

  const handleReady = useCallback(() => {
    onReady?.(waveInfoUrl);
  }, [onReady, waveInfoUrl]);

  // Re-attach peaks whenever wave_info_url changes — notably after
  // regenerate-audio-timeline swaps it to a new versioned blob. ReactPlayer's
  // onReady only fires once per media mount, so without this the editor stays
  // on the old waveform even after the backend finished the new one.
  useEffect(() => {
    onReady?.(waveInfoUrl);
  }, [onReady, waveInfoUrl]);

  const fallback = useMemo(
    () => (
      <div className="flex justify-center items-center h-full">
        <Mic width={28} aria-hidden="true" />
      </div>
    ),
    []
  );

  return (
    <div
      className={cn(
        'relative w-full flex-shrink-0 border overflow-hidden rounded-t-2xl',
        aspectClass,
        className
      )}
      style={{
        backgroundColor: getColour('secondLayer'),
        color: getColour('icon'),
        borderColor: getColour('border')
      }}
    >
      <ReactPlayer
        ref={ref}
        url={mediaUrl ?? undefined}
        controls={false}
        config={playerConfig}
        onPlay={play}
        onPause={onMediaPause}
        playing={isPlaying}
        volume={1}
        muted={isMuted}
        onProgress={onProgress}
        playbackRate={playbackRate}
        autoPlay={false}
        progressInterval={300}
        onDuration={setDuration}
        width={'100%'}
        height={'100%'}
        fallback={fallback}
        onReady={handleReady}
      />

      {showAudioThumbnail && (
        <div className="absolute inset-0 flex justify-center items-center h-full">
          <img src={audioThumbnail} alt="" width={150} />
        </div>
      )}

      {!hasMedia && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
          <div
            className="flex items-center gap-2 px-3 py-1.5 rounded-full border"
            style={{
              backgroundColor: 'rgba(245, 158, 11, 0.12)',
              borderColor: 'rgba(245, 158, 11, 0.5)',
              color: 'rgba(245, 158, 11, 1)'
            }}
          >
            <AlertCircle size={14} aria-hidden="true" />
            <span className="text-xs font-medium">
              {t('no-media-available')}
            </span>
          </div>
          <p
            className="text-xs max-w-xs opacity-80"
            style={{ color: getColour('inactiveText') }}
          >
            {t('no-media-description')}
          </p>
          {sessionId && (
            <button
              onClick={() => setIsMediaSourceOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-2xl text-xs font-medium transition-all hover:opacity-90 active:scale-95"
              style={{
                backgroundColor: getColour('activeText'),
                color: '#fff'
              }}
            >
              <Link2 size={14} aria-hidden="true" />
              {t('no-media-add-source')}
            </button>
          )}
        </div>
      )}

      {overlay}

      {isMediaSourceOpen && sessionId && (
        <MediaSourceModal
          isOpen={isMediaSourceOpen}
          onClose={() => setIsMediaSourceOpen(false)}
          sessionId={sessionId}
          currentMediaUrl={mediaUrl ?? undefined}
          session={session}
        />
      )}
    </div>
  );
}
